<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Create Medication </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <div class="mb-4">
              <v-text-field
                v-model="formData.medication_name"
                :rules="[(v) => !!v || 'Medication Name is required']"
                label="Medication Name"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              />

              <v-select
                v-model="formData.medication_category_id"
                :items="categories"
                :item-title="(c) => formatMedicationCategoryName(c.medication_category_name)"
                item-value="medication_category_id"
                :rules="[(v) => !!v || 'Category is required']"
                label="Category"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
                @update:model-value="onCategoryChange"
              />

              <v-select
                v-if="hasMedicationClasses"
                v-model="formData.medication_class_id"
                :items="filteredMedicationClasses"
                item-title="class_name"
                item-value="class_id"
                :rules="[(v) => !!v || 'Medication Class is required']"
                label="Medication Class"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              />

              <v-text-field
                v-model="formData.brand_name"
                label="Brand Name"
                variant="outlined"
                density="compact"
                class="mb-2"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!valid || loading"
            :loading="loading"
            @click="handleSubmit"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { create_master_medications } from "@/graphql/mutations";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";
import GetMedicationClasses from "@/mixins/Medication/GetMedicationClasses";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

export default {
  name: "CreateMedicationmain",
  mixins: [GetMedicationCategories, GetMedicationClasses],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      valid: false,
      loading: false,
      categories: [],
      formData: {
        medication_name: "",
        medication_category_id: null,
        medication_class_id: null,
        brand_name: "",
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    filteredMedicationClasses() {
      if (!this.formData.medication_category_id) {
        return [];
      }
      return this.medicationClassesList.filter(
        (item) =>
          String(item.medication_category_id) ===
          String(this.formData.medication_category_id)
      );
    },
    hasMedicationClasses() {
      return this.filteredMedicationClasses.length > 0;
    },
    selectedMedicationClass() {
      return this.medicationClassesList.find(
        (item) =>
          String(item.class_id) === String(this.formData.medication_class_id)
      );
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadData();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    formatMedicationCategoryName,
    async loadData() {
      try {
        await Promise.all([
          this.fetchMedicationCategories(),
          this.fetchMedicationClasses(),
        ]);
        this.categories = this.medicationCategoriesList.map((item) => ({
          medication_category_id: item.medication_category_id,
          medication_category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication data:", error);
      }
    },
    onCategoryChange() {
      this.formData.medication_class_id = null;
    },
    getSelectedCategoryName() {
      const selectedCategory = this.categories.find(
        (category) =>
          String(category.medication_category_id) ===
          String(this.formData.medication_category_id)
      );
      return selectedCategory?.medication_category_name || "";
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        medication_name: "",
        medication_category_id: null,
        medication_class_id: null,
        brand_name: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (this.hasMedicationClasses && !this.formData.medication_class_id) {
        return;
      }

      this.loading = true;

      try {
        const selectedClass = this.selectedMedicationClass;
        const input = {
          medication_name: this.formData.medication_name.trim(),
          medication_category_id: this.formData.medication_category_id,
          medication_category_name: this.getSelectedCategoryName(),
          medication_class: selectedClass?.class_name || "",
          medication_class_id: selectedClass?.class_id || "",
          brand_name: this.formData.brand_name || "",
        };

        const response = await client.graphql({
          query: create_master_medications,
          variables: {
            input,
          },
        });

        const responseData = JSON.parse(response.data.create_master_medications);

        if (responseData.status == "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Medication created successfully",
            color: "success",
          };
          this.$emit("created", 0);
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to create medication",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error creating medication:", error);
        this.snackbar = {
          show: true,
          message:
            error.message || "An error occurred while creating the medication",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
